import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useEffect, useRef } from 'react';
import { Logger } from '../../support/debug/Logger.ts';
import { AlertPresenter } from '../../app/AlertBinder.tsx';
import { MapViewModel } from '../model/MapViewModel.ts';
import { VisibleMapLayersState } from '../toggle-layers/VisibleMapLayersState.ts';

/**
 * <BindMapViewModel /> binds a dom element to an instance of mapbox gl js
 * and initializes the map with a base style and other general configuration.
 * MapViewModel should not be used until await container.get(MapViewModel).asyncInitialized()
 *
 * To limit that temporal coupling, see <MapViewModelBoundContainer>
 *
 * This is the lower level building block it uses to actually bind and init the map.
 */
export const BindMapViewModel = () => {
    const log = useInstance(Logger);
    const alert = useInstance(AlertPresenter);

    const elRef = useRef(null);
    const mapViewModel = useInstance(MapViewModel);
    const mapVisibleLayers = useInstance(VisibleMapLayersState);

    useEffect(() => {
        if (mapViewModel.isDisabled()){
            return;
        }

        if (elRef.current) {
            mapViewModel.initMap(elRef.current as HTMLElement)
                .then(() => {
                    // seams has made map layers a bit laggy,
                    // so use a timeout so the map appears quicker
                    setTimeout(mapVisibleLayers.init, 500);
                })
                .catch((e) => {
                    e.message = `initMap() failed: ${e.message}`;
                    if (!mapViewModel.isInitialized()) {
                        e.message = `App did not start! ${e.message}`;
                        alert.showAlert({
                            header: 'AutoPylot is unable to start',
                            message: `<p>Sorry, we were unable to initialize the map, so the app has failed to start.</p><p>Please contact us if you continue to have this problem.</p>`
                        });
                    }
                    log.error(e);
                });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [elRef, mapViewModel, mapVisibleLayers]);

    return <div id="permanent-map-container" style={{ display: 'none' }} ref={elRef} />;
};
